import { useDispatch } from 'react-redux';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import { Typography, Button, Stack, FormControl, OutlinedInput, Divider, Collapse, IconButton } from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAddProductMutation } from '../features/api/openFoodFacts/apiFoodFactsSlice';
import Spinner from './Spinner';
import AddProductPhoto from './AddProductPhoto';
import { setAlertMessage } from '../features/layoutSlice';

function AddProduct() {
  const ExpandMore = styled((props) => {
    // eslint-disable-next-line react/prop-types
    const { expand, ...other } = props;
    return <IconButton {...other} />; //    eslint-disable-line react/jsx-props-no-spreading
  })(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  }));

  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [brands, setBrands] = useState('');
  const [quantity, setQuantity] = useState('');
  const [ingredients, setIngredients] = useState('');
  const [packaging, setPackaging] = useState('');
  const [categories, setCategories] = useState('');
  const [labels, setLabels] = useState('');
  const [stores, setStores] = useState('');
  const [countries, setCountries] = useState('');

  const [expandedIngredients, setExpandedIngredients] = useState(false);
  const [expandedPackaging, setExpandedPackaging] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(false);
  const [expandedLabels, setExpandedLabels] = useState(false);
  const [expandedStores, setExpandedStores] = useState(false);
  const [expandedCountries, setExpandedCountries] = useState(false);

  const [addProduct, { isLoading }] = useAddProductMutation();
  const { t } = useTranslation();
  const { ean } = useParams();
  const navigate = useNavigate();

  //  User_id and password are optional for a new Product, but used for update a Product
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addProduct({
        code: ean,
        name,
        quantity,
        packaging,
        brands,
        categories,
        labels,
        stores,
        countries,
        ingredients,
      }).unwrap();
      navigate(`/products/${ean}`);
    } catch (err) {
      dispatch(setAlertMessage({ text: t('products.messages.databaseError'), type: 'error' }));
    }
  };

  const handleExpandClick = (type) => {
    if (type === 'ingredients') setExpandedIngredients(!expandedIngredients);
    if (type === 'packaging') setExpandedPackaging(!expandedPackaging);
    if (type === 'categories') setExpandedCategories(!expandedCategories);
    if (type === 'labels') setExpandedLabels(!expandedLabels);
    if (type === 'stores') setExpandedStores(!expandedStores);
    if (type === 'countries') setExpandedCountries(!expandedCountries);
  };

  if (isLoading) return <Spinner />;

  return (
    <Stack component="form" onSubmit={handleSubmit} maxWidth="40%">
      <Typography variant="h5" sx={{ fontWeight: 'bold', pb: 2, mt: 2 }}>
        {t('products.form.addProduct.heading', { ean })}
      </Typography>
      <Typography variant="h6" align="left" sx={{ pb: 1 }}>
        {t('products.form.addProduct.fields.name')}
      </Typography>
      <FormControl>
        <OutlinedInput sx={{ borderRadius: 0, mb: 2 }} type="text" id="name" placeholder={t('products.form.addProduct.placeholders.name')} required value={name} onChange={(e) => setName(e.target.value)} />
      </FormControl>
      <Typography variant="h6" align="left" sx={{ pb: 1 }}>
        {t('products.form.addProduct.fields.images')}
      </Typography>
      <AddProductPhoto />
      <Typography variant="h6" align="left" sx={{ pb: 1 }}>
        {t('products.form.addProduct.fields.brand')}
      </Typography>
      <FormControl>
        <OutlinedInput sx={{ borderRadius: 0, mb: 2 }} type="text" id="brands" placeholder={t('products.form.addProduct.placeholders.brand')} value={brands} onChange={(e) => setBrands(e.target.value)} />
      </FormControl>
      <Typography variant="h6" align="left" sx={{ pb: 1 }}>
        {t('products.form.addProduct.fields.quantity')}
      </Typography>
      <FormControl>
        <OutlinedInput sx={{ borderRadius: 0, mb: 2 }} type="text" id="quantity" placeholder={t('products.form.addProduct.placeholders.quantity')} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
      </FormControl>
      <Divider variant="middle" sx={{ mb: 2, mt: 1 }} />
      <Typography variant="h6" align="left" sx={{ pb: 1 }}>
        {t('products.form.addProduct.fields.ingredients')}
        <ExpandMore expand={expandedIngredients} onClick={() => handleExpandClick('ingredients')} aria-expanded={expandedIngredients} aria-label={t('aria.icons.showMore')}>
          <ExpandMoreIcon />
        </ExpandMore>
      </Typography>
      <Collapse in={expandedIngredients} timeout="auto" unmountOnExit>
        <FormControl sx={{ width: '100%' }}>
          <OutlinedInput sx={{ borderRadius: 0, mb: 2 }} type="text" id="labels" placeholder={t('products.form.addProduct.placeholders.ingredients')} value={ingredients} onChange={(e) => setIngredients(e.target.value)} />
        </FormControl>
      </Collapse>
      <Divider variant="middle" sx={{ mb: 2, mt: 1 }} />
      <Typography variant="h6" align="left" sx={{ pb: 1 }}>
        {t('products.form.addProduct.fields.packaging')}
        <ExpandMore expand={expandedPackaging} onClick={() => handleExpandClick('packaging')} aria-expanded={expandedPackaging} aria-label={t('aria.icons.showMore')}>
          <ExpandMoreIcon />
        </ExpandMore>
      </Typography>
      <Collapse in={expandedPackaging} timeout="auto" unmountOnExit>
        <FormControl sx={{ width: '100%' }}>
          <OutlinedInput sx={{ borderRadius: 0, mb: 2 }} type="text" id="name" placeholder={t('products.form.addProduct.placeholders.packaging')} value={packaging} onChange={(e) => setPackaging(e.target.value)} />
        </FormControl>
      </Collapse>
      <Divider variant="middle" sx={{ mb: 2, mt: 1 }} />
      <Typography variant="h6" align="left" sx={{ pb: 1 }}>
        {t('products.form.addProduct.fields.categories')}
        <ExpandMore expand={expandedCategories} onClick={() => handleExpandClick('categories')} aria-expanded={expandedCategories} aria-label={t('aria.icons.showMore')}>
          <ExpandMoreIcon />
        </ExpandMore>
      </Typography>
      <Collapse in={expandedCategories} timeout="auto" unmountOnExit>
        <FormControl sx={{ width: '100%' }}>
          <OutlinedInput sx={{ borderRadius: 0, mb: 2 }} type="text" id="categories" placeholder={t('products.form.addProduct.placeholders.categories')} value={categories} onChange={(e) => setCategories(e.target.value)} />
        </FormControl>
      </Collapse>
      <Divider variant="middle" sx={{ mb: 2, mt: 1 }} />
      <Typography variant="h6" align="left" sx={{ pb: 1 }}>
        {t('products.form.addProduct.fields.labels')}
        <ExpandMore expand={expandedLabels} onClick={() => handleExpandClick('labels')} aria-expanded={expandedLabels} aria-label={t('aria.icons.showMore')}>
          <ExpandMoreIcon />
        </ExpandMore>
      </Typography>
      <Collapse in={expandedLabels} timeout="auto" unmountOnExit>
        <FormControl sx={{ width: '100%' }}>
          <OutlinedInput sx={{ borderRadius: 0, mb: 2 }} type="text" id="labels" placeholder={t('products.form.addProduct.placeholders.labels')} value={labels} onChange={(e) => setLabels(e.target.value)} />
        </FormControl>
      </Collapse>
      <Divider variant="middle" sx={{ mb: 2, mt: 1 }} />
      <Typography variant="h6" align="left" sx={{ pb: 1 }}>
        {t('products.form.addProduct.fields.stores')}
        <ExpandMore expand={expandedStores} onClick={() => handleExpandClick('stores')} aria-expanded={expandedStores} aria-label={t('aria.icons.showMore')}>
          <ExpandMoreIcon />
        </ExpandMore>
      </Typography>
      <Collapse in={expandedStores} timeout="auto" unmountOnExit>
        <FormControl sx={{ width: '100%' }}>
          <OutlinedInput sx={{ borderRadius: 0, mb: 2 }} type="text" id="labels" placeholder={t('products.form.addProduct.placeholders.stores')} value={stores} onChange={(e) => setStores(e.target.value)} />
        </FormControl>
      </Collapse>
      <Divider variant="middle" sx={{ mb: 2, mt: 1 }} />
      <Typography variant="h6" align="left" sx={{ pb: 1 }}>
        {t('products.form.addProduct.fields.countries')}
        <ExpandMore expand={expandedCountries} onClick={() => handleExpandClick('countries')} aria-expanded={expandedCountries} aria-label={t('aria.icons.showMore')}>
          <ExpandMoreIcon />
        </ExpandMore>
      </Typography>
      <Collapse in={expandedCountries} timeout="auto" unmountOnExit>
        <FormControl sx={{ width: '100%' }}>
          <OutlinedInput sx={{ borderRadius: 0, mb: 2 }} type="text" id="labels" placeholder={t('products.form.addProduct.placeholders.countries')} value={countries} onChange={(e) => setCountries(e.target.value)} />
        </FormControl>
      </Collapse>
      <Divider variant="middle" sx={{ mb: 2, mt: 1 }} />
      <Button type="submit" variant="contained" sx={{ mr: 1, mb: 2, position: 'static' }}>
        {t('common.actions.send')}
      </Button>
    </Stack>
  );
}

export default AddProduct;
